import React from 'react';
import { useLocation, useParams } from 'react-router';
import LeadGenCard from './LeadGenCard';
import { Grid, Typography, Box, Paper, Stack, Chip, LinearProgress, Alert, Card, CardContent } from '@mui/material';
import { useQuery, gql } from '@apollo/client';
import LeadGenDataTable from 'Components/LeadGenDataTable';
import MainCard from 'ui-component/cards/MainCard';
import { Business as BusinessIcon, People as PeopleIcon, QrCodeScanner as QrCodeIcon } from '@mui/icons-material';

// Define the GraphQL query
const GET_SCAN_DATA_BY_STAND_ID = gql`
    query GetScanDataByStandId($eventId: String!, $standId: String!) {
        ScanDataByStandId(eventId: $eventId, standId: $standId)
    }
`;

const GET_STAND_DETAILS = gql`
    query GetStandDetails($eventId: String!) {
        standsByEventId(eventId: $eventId)
    }
`;

export default function LeadGenData() {
    const location = useLocation();
    const { exhibitorId, eventId } = useParams();
    const standId = exhibitorId;

    // Access the state passed during navigation
    const { state } = location;
    const standData = state && state.stand;

    const { loading, error, data } = useQuery(GET_SCAN_DATA_BY_STAND_ID, {
        variables: { eventId, standId },
    });

    const { data: standsData } = useQuery(GET_STAND_DETAILS, {
        variables: { eventId },
        skip: !standData,
    });

    // Find the stand from stands data if not in state
    let currentStand = standData;
    if (!currentStand && standsData?.standsByEventId) {
        try {
            const stands = JSON.parse(standsData.standsByEventId);
            currentStand = stands.find(s => s.id === standId);
        } catch (e) {
            console.error('Error parsing stands data:', e);
        }
    }

    const ScannedData = (data && data?.ScanDataByStandId && JSON.parse(data?.ScanDataByStandId)) || [];
    const totalScans = ScannedData?.length || 0;
    const staffCount = currentStand?.staff?.length || 0;

    if (loading) {
        return (
            <MainCard title='Stand Details'>
                <LinearProgress />
            </MainCard>
        );
    }

    if (error) {
        return (
            <MainCard title='Stand Details'>
                <Alert severity='error'>{error.message}</Alert>
            </MainCard>
        );
    }

    return (
        <MainCard title={currentStand?.company || 'Stand Details'}>
            <Stack spacing={3}>
                {/* Stand Summary */}
                {currentStand && (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            bgcolor: 'rgba(102, 126, 234, 0.08)',
                            border: '1px solid',
                            borderColor: 'rgba(102, 126, 234, 0.2)',
                            borderRadius: 2,
                        }}
                    >
                        <Stack direction='row' spacing={3} alignItems='center' flexWrap='wrap'>
                            <Box>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                    sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                                >
                                    Company Name
                                </Typography>
                                <Typography variant='h6' fontWeight={700} sx={{ mt: 0.5 }}>
                                    {currentStand.company}
                                </Typography>
                            </Box>
                            {currentStand.stallNo && (
                                <Box>
                                    <Typography
                                        variant='caption'
                                        color='text.secondary'
                                        sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                                    >
                                        Stall Number
                                    </Typography>
                                    <Typography variant='h6' fontWeight={700} sx={{ mt: 0.5 }}>
                                        {currentStand.stallNo}
                                    </Typography>
                                </Box>
                            )}
                            <Box>
                                <Stack direction='row' spacing={2} alignItems='center'>
                                    <Chip
                                        icon={<PeopleIcon />}
                                        label={`${staffCount} Staff`}
                                        color='primary'
                                        variant='outlined'
                                    />
                                    <Chip
                                        icon={<QrCodeIcon />}
                                        label={`${totalScans} Scans`}
                                        color='success'
                                        variant='outlined'
                                    />
                                </Stack>
                            </Box>
                        </Stack>
                    </Paper>
                )}

                {/* Staff Members */}
                {currentStand && currentStand.staff && currentStand.staff.length > 0 && (
                    <Box>
                        <Typography variant='h6' gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            Staff Members ({currentStand.staff.length})
                        </Typography>
                        <Grid container spacing={2}>
                            {currentStand.staff.map((staff, index) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={staff.id || index}>
                                    <LeadGenCard stand={staff} type='attendee' />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {/* Data Table */}
                <Box>
                    <Typography variant='h6' gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                        Lead Generation Data
                    </Typography>
                    <LeadGenDataTable type='admin' id={standId} />
                </Box>
            </Stack>
        </MainCard>
    );
}
